import React, { FC, useState, ChangeEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    TextField,
    MenuItem,
    Select,
    SelectChangeEvent,
    InputLabel,
} from '@mui/material';
import { DeviationType } from 'models/deviation';

type AddUpdateDeviationTypeDialogProps = {
    deviation?: DeviationType;
    open: boolean;
    onDialogClose: () => void;
    onDeviationAdd: (deviationType: DeviationType) => void;
    onDeviationUpdate: (deviationType: DeviationType) => void;
};

const AddUpdateDeviationTypeDialog: FC<AddUpdateDeviationTypeDialogProps> = ({
    deviation,
    open,
    onDialogClose,
    onDeviationAdd,
    onDeviationUpdate,
}: AddUpdateDeviationTypeDialogProps) => {
    const { t: translate } = useTranslation(['common', 'deviations']);
    const [deviationType, setDeviationType] = useState<DeviationType>({} as DeviationType);

    useEffect(() => {
        if (deviation) {
            setDeviationType(deviation);
        }
    }, [deviation]);

    const handleChangeCode = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setDeviationType({ ...deviationType, [event.target.name]: value });
    };

    const handleChangeDescription = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setDeviationType({ ...deviationType, [event.target.name]: value });
    };

    const handleTypeChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        setDeviationType({ ...deviationType, [event.target.name]: value });
    };

    const handleDialogClose = () => {
        onDialogClose();
    };

    const handleAddType = () => {
        onDeviationAdd(deviationType);
    };

    const handleUpdateType = () => {
        onDeviationUpdate(deviationType);
    };

    return (
        <Dialog maxWidth="sm" fullWidth open={open}>
            {deviationType?._id ? (
                <DialogTitle>{translate('deviations:EDIT_DEVIATION_TYPE')}</DialogTitle>
            ) : (
                <DialogTitle>{translate('deviations:ADD_DEVIATION_TYPE')}</DialogTitle>
            )}

            <DialogContent>
                <Box mt={1}>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <FormControl margin="dense" fullWidth>
                                <TextField
                                    value={deviationType?.code}
                                    name="code"
                                    onChange={handleChangeCode}
                                    required
                                    id="code"
                                    label={translate('deviations:DEVIATION_CODE')}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl margin="dense" fullWidth>
                                <TextField
                                    value={deviationType?.description}
                                    name="description"
                                    onChange={handleChangeDescription}
                                    required
                                    id="description"
                                    label={translate('common:DESCRIPTION')}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl margin="dense" fullWidth>
                                <InputLabel id="type-label">{translate('deviations:TYPE')}</InputLabel>
                                <Select
                                    labelId="type-label"
                                    name="type"
                                    required
                                    label={translate('deviations:TYPE')}
                                    value={deviationType.type || ''}
                                    onChange={handleTypeChange}
                                >
                                    <MenuItem value="INTERNAL">{translate('deviations:INTERNAL')}</MenuItem>
                                    <MenuItem value="EXTERNAL">{translate('deviations:EXTERNAL')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleDialogClose}>{translate('common:CANCEL')}</Button>
                <Button
                    disabled={!deviationType.code || !deviationType.description || !deviationType.type}
                    onClick={deviationType?._id ? handleUpdateType : handleAddType}
                    variant="contained"
                    type="submit"
                >
                    {deviationType?._id ? translate('common:UPDATE') : translate('common:ADD')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddUpdateDeviationTypeDialog;
